/**
 * Painel do servidor (Painel Berytus → Servidor e API), para o suporte: máquina, processo da API, banco e fila do worker;
 * requisições no tempo, rotas mais chamadas, quem mais chama (cliente e chave da API, pico por minuto e recusas por
 * limite) e os últimos erros. Atualiza sozinho a cada 30 s.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useUrlState } from './MeliChannel.tsx';

interface Who { tenantId: string | null; tenant: string | null; keyId: string | null; key: string | null }
interface ServerData {
  hours: number; bin: string;
  limits: { ordersPerMinute: number; otherPerMinute: number };
  machine: { cpu: number | null; cores: number; load: number[]; memTotal: number; memFree: number; uptime: number };
  process: { rss: number; heapUsed: number; heapTotal: number; uptime: number; node: string; pid: number; eventLoopP99Ms: number; eventLoopMaxMs: number };
  database: { bytes: number; connections: { state: string; count: number }[] };
  queue: { counts: Record<string, number>; failedByType: { type: string; count: number; lastError: string | null; lastAt: number | null }[]; activeByType: { type: string; count: number }[] } | null;
  totals: { count: number; errors5xx: number; errors4xx: number; rateLimited: number; avgMs: number | null };
  series: { at: string; count: number; errors: number; limited: number }[];
  routes: { surface: string; method: string; route: string; count: number; errors5xx: number; errors4xx: number; rateLimited: number; avgMs: number | null; maxMs: number }[];
  callers: (Who & { surface: string; count: number; rateLimited: number; errors5xx: number; peakPerMinute: number; topRoute: string | null })[];
  errors: (Who & { at: string; surface: string; method: string; url: string; status: number; message: string | null; ms: number | null })[];
}

const HOURS: [number, string][] = [[1, '1 hora'], [6, '6 horas'], [24, '24 horas'], [168, '7 dias']];
const SURFACE: Record<string, string> = { public: 'API do cliente', panel: 'Painel', webhook: 'Notificações', other: 'Outros' };
const gb = (b: number) => `${(b / 1024 ** 3).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`;
const mb = (b: number) => `${Math.round(b / 1024 ** 2).toLocaleString('pt-BR')} MB`;
const pct = (v: number) => `${Math.round(v * 100)}%`;
const dur = (s: number) => (s >= 86400 ? `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h` : s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}min` : `${Math.floor(s / 60)}min`);

export function AdminServer() {
  const [params, set] = useUrlState();
  const hours = Number(params.get('h') ?? 1);
  const q = useQuery({
    queryKey: ['admin', 'server', hours],
    queryFn: () => api<ServerData>(`/admin/server?hours=${hours}`),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const d = q.data;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Servidor e API</h1>
          <p>Máquina, API, banco e fila; quem chama a API, em que rota, quantas vezes, recusas por limite e erros. Atualiza a cada 30 segundos.</p>
        </div>
      </div>
      <div className="stack-y">
        <div className="seg" role="group" aria-label="Período" style={{ justifySelf: 'start' }}>
          {HOURS.map(([h, l]) => <button key={h} type="button" aria-pressed={hours === h} onClick={() => set({ h: h === 1 ? null : String(h) })}>{l}</button>)}
        </div>
        {q.error ? <ErrorBox error={q.error} /> : !d ? <Loading /> : (
          <div className="stack-y" style={{ opacity: q.isPlaceholderData ? 0.6 : 1 }}>
            <div className="kpis">
              <Kpi label="Requisições" value={int(d.totals.count)} note={`média ${d.totals.avgMs ?? '—'} ms · ${int(Math.round(d.totals.count / (d.hours * 60)))}/min`} />
              <Kpi label="Recusadas por limite (429)" value={int(d.totals.rateLimited)} alert={d.totals.rateLimited > 0}
                note={`limite: ${int(d.limits.ordersPerMinute)}/min pedidos · ${int(d.limits.otherPerMinute)}/min resto, por chave`} />
              <Kpi label="Erros do servidor (5xx)" value={int(d.totals.errors5xx)} alert={d.totals.errors5xx > 0} note="falhas da Berytus" />
              <Kpi label="Erros do cliente (4xx)" value={int(d.totals.errors4xx)} note="pedido inválido, sem permissão, não encontrado" />
            </div>

            <section className="panel stack-y" style={{ gap: 8 }}>
              <h2 style={{ margin: 0 }}>Requisições no tempo <span className="small muted">(a cada {d.bin.replace('minutes', 'min').replace('minute', 'min').replace('hour', 'hora')})</span></h2>
              <Series rows={fill(d.series, d.hours, d.bin)} />
            </section>

            <div className="cols">
              <section className="panel stack-y" style={{ gap: 6 }}>
                <h2 style={{ margin: 0 }}>Servidor</h2>
                <Line k="CPU">{d.machine.cpu == null ? '—' : pct(d.machine.cpu)} de {d.machine.cores} núcleos · carga {d.machine.load.join(' / ')}</Line>
                <Line k="Memória">{gb(d.machine.memTotal - d.machine.memFree)} usados de {gb(d.machine.memTotal)} ({pct(1 - d.machine.memFree / d.machine.memTotal)})</Line>
                <Line k="Ligado há">{dur(d.machine.uptime)}</Line>
                <Line k="API (este processo)">{mb(d.process.rss)} · heap {mb(d.process.heapUsed)} · no ar há {dur(d.process.uptime)} · Node {d.process.node}</Line>
                <Line k="Atraso do event loop">
                  <span style={d.process.eventLoopP99Ms > 200 ? { color: 'var(--err)', fontWeight: 600 } : undefined}>p99 {d.process.eventLoopP99Ms} ms · máx. {d.process.eventLoopMaxMs} ms</span>
                </Line>
                <Line k="Banco">{gb(d.database.bytes)} · conexões {d.database.connections.map((c) => `${c.state} ${c.count}`).join(' · ')}</Line>
              </section>
              <section className="panel stack-y" style={{ gap: 6 }}>
                <h2 style={{ margin: 0 }}>Fila do worker</h2>
                {!d.queue ? <span className="small muted">Fila indisponível (Redis fora do ar ou sem resposta em 3 s).</span> : (
                  <>
                    <Line k="Agora">rodando {int(d.queue.counts.active ?? 0)} · aguardando {int((d.queue.counts.waiting ?? 0) + (d.queue.counts.prioritized ?? 0))} · agendadas {int(d.queue.counts.delayed ?? 0)}</Line>
                    {d.queue.activeByType.length > 0 && <Line k="Por tipo">{d.queue.activeByType.slice(0, 6).map((a) => `${a.type} ${a.count}`).join(' · ')}</Line>}
                    <Line k="Falhas (24 h)">{d.queue.failedByType.length ? '' : <span className="muted">nenhuma</span>}</Line>
                    {d.queue.failedByType.slice(0, 8).map((f) => (
                      <div key={f.type} className="small" style={{ display: 'grid', gap: 2 }}>
                        <span><Tag tone="err">{f.count}</Tag> <b>{f.type}</b>{f.lastAt && <span className="muted"> · {dateTime(new Date(f.lastAt).toISOString())}</span>}</span>
                        {f.lastError && <span className="muted" style={{ whiteSpace: 'normal' }}>{f.lastError}</span>}
                      </div>
                    ))}
                  </>
                )}
              </section>
            </div>

            <section className="panel flush">
              <h2 style={{ margin: 0, padding: '14px 16px' }}>Quem mais chama</h2>
              <Table cols={['Origem', 'Cliente', 'Chave da API', 'Requisições', 'Pico/min', '429', '5xx', 'Rota mais chamada']} widths={[110, 170, 200, 115, 90, 70, 70, 0]} empty="Sem requisições no período.">
                {d.callers.map((c, i) => {
                  const limit = c.topRoute && /\/(orders|packs|ping)(\/|$)/.test(c.topRoute) ? d.limits.ordersPerMinute : d.limits.otherPerMinute;
                  return (
                    <tr key={i} style={{ cursor: 'default' }}>
                      <td>{SURFACE[c.surface] ?? c.surface}</td>
                      <td>{c.tenantId ? <Link to={`/admin/clientes/${c.tenantId}`}>{c.tenant}</Link> : <span className="muted">—</span>}</td>
                      <td className="small">{c.key ?? <span className="muted">{c.surface === 'panel' ? 'usuários do painel' : '—'}</span>}</td>
                      <td className="num">{int(c.count)}</td>
                      <td className="num" style={c.surface === 'public' && c.peakPerMinute >= limit * 0.8 ? { color: 'var(--err)', fontWeight: 600 } : undefined}
                        title={c.surface === 'public' ? `limite ${int(limit)}/min` : undefined}>{int(c.peakPerMinute)}</td>
                      <td className="num" style={c.rateLimited ? { color: 'var(--err)', fontWeight: 600 } : undefined}>{int(c.rateLimited)}</td>
                      <td className="num" style={c.errors5xx ? { color: 'var(--err)' } : undefined}>{int(c.errors5xx)}</td>
                      <td className="small code wrap" style={{ wordBreak: 'break-all' }}>{c.topRoute ?? '—'}</td>
                    </tr>
                  );
                })}
              </Table>
            </section>

            <section className="panel flush">
              <h2 style={{ margin: 0, padding: '14px 16px' }}>Rotas mais chamadas</h2>
              <Table cols={['Origem', 'Rota', 'Requisições', 'Média', 'Máx.', '429', '4xx', '5xx']} widths={[120, 0, 115, 90, 90, 70, 70, 70]} empty="Sem requisições no período.">
                {d.routes.map((r, i) => (
                  <tr key={i} style={{ cursor: 'default' }}>
                    <td className="small">{SURFACE[r.surface] ?? r.surface}</td>
                    <td className="small code wrap" style={{ wordBreak: 'break-all' }}><b>{r.method}</b> {r.route}</td>
                    <td className="num">{int(r.count)}</td>
                    <td className="num" style={(r.avgMs ?? 0) > 1000 ? { color: 'var(--warn)', fontWeight: 600 } : undefined}>{r.avgMs ?? '—'} ms</td>
                    <td className="num">{int(r.maxMs)} ms</td>
                    <td className="num" style={r.rateLimited ? { color: 'var(--err)' } : undefined}>{int(r.rateLimited)}</td>
                    <td className="num">{int(r.errors4xx)}</td>
                    <td className="num" style={r.errors5xx ? { color: 'var(--err)', fontWeight: 600 } : undefined}>{int(r.errors5xx)}</td>
                  </tr>
                ))}
              </Table>
            </section>

            <section className="panel flush">
              <h2 style={{ margin: 0, padding: '14px 16px' }}>Últimos erros do servidor (5xx)</h2>
              <Table cols={['Quando', 'Cliente', 'Chave', 'Requisição', 'Situação', 'Tempo', 'Mensagem']} widths={[140, 150, 170, 0, 80, 80, 0]} empty="Nenhum erro no período.">
                {d.errors.map((e, i) => (
                  <tr key={i} style={{ cursor: 'default' }}>
                    <td className="small">{dateTime(e.at)}</td>
                    <td className="small">{e.tenant ?? '—'}</td>
                    <td className="small">{e.key ?? '—'}</td>
                    <td className="small code wrap" style={{ wordBreak: 'break-all' }}><b>{e.method}</b> {e.url}</td>
                    <td><Tag tone="err">{e.status}</Tag></td>
                    <td className="num small">{e.ms ?? '—'} ms</td>
                    <td className="small wrap">{e.message ?? '—'}</td>
                  </tr>
                ))}
              </Table>
            </section>
          </div>
        )}
      </div>
    </>
  );
}

function Kpi({ label, value, note, alert }: { label: string; value: string; note: string; alert?: boolean }) {
  return <div className={`kpi${alert ? ' alert' : ''}`}><span className="k-label">{label}</span><span className="k-value">{value}</span><span className="k-note">{note}</span></div>;
}

function Line({ k, children }: { k: string; children: React.ReactNode }) {
  return <div className="row-line small"><span className="muted">{k}</span><span style={{ textAlign: 'right' }}>{children}</span></div>;
}

/** widths: largura de cada coluna em px; 0 = divide o que sobra. */
function Table({ cols, widths, empty, children }: { cols: string[]; widths: number[]; empty: string; children: React.ReactNode[] }) {
  if (!children.length) return <div className="empty small" style={{ padding: 16 }}>{empty}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="grid" style={{ width: '100%', minWidth: 960 }}>
        <colgroup>{widths.map((w, i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
        <thead><tr>{cols.map((c, i) => <th key={c} className={i >= 3 && !/Rota|Mensagem|Requisição|Situação|Quando/.test(c) ? 'num' : undefined}>{c}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** Completa a série com os intervalos sem requisição, para a barra mostrar o período inteiro. */
function fill(rows: ServerData['series'], hours: number, bin: string): ServerData['series'] {
  const step = bin.startsWith('1 hour') ? 3_600_000 : bin.startsWith('10') ? 600_000 : 60_000;
  const byAt = new Map(rows.map((r) => [new Date(r.at).getTime(), r]));
  const end = Math.floor(Date.now() / step) * step;
  const out: ServerData['series'] = [];
  for (let at = end - Math.ceil((hours * 3_600_000) / step - 1) * step; at <= end; at += step) {
    out.push(byAt.get(at) ?? { at: new Date(at).toISOString(), count: 0, errors: 0, limited: 0 });
  }
  return out;
}

/** Barras por intervalo: requisições (cinza), com a parte recusada por limite (vermelho claro) e os erros (vermelho). */
function Series({ rows }: { rows: ServerData['series'] }) {
  if (!rows.length) return <span className="small muted">Sem requisições no período.</span>;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 120 }} role="img" aria-label="Requisições no tempo">
        {rows.map((r) => (
          <div key={r.at} title={`${dateTime(r.at)}: ${int(r.count)} requisições · ${int(r.limited)} recusadas (429) · ${int(r.errors)} erros`}
            style={{ flex: 1, minWidth: 2, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
            <div style={{ height: `${(r.errors / max) * 100}%`, background: 'var(--err)' }} />
            <div style={{ height: `${(r.limited / max) * 100}%`, background: 'color-mix(in srgb, var(--err) 45%, transparent)' }} />
            <div style={{ height: `${(Math.max(0, r.count - r.errors - r.limited) / max) * 100}%`, background: 'var(--muted)', opacity: 0.55 }} />
          </div>
        ))}
      </div>
      <div className="small muted" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span>{dateTime(rows[0]!.at)}</span><span>pico {int(max)} por intervalo</span><span>{dateTime(rows.at(-1)!.at)}</span>
      </div>
    </div>
  );
}
